// render: the sponsor creative for every format, and the "Sponsor this spot" open spot.
// One set of shared parts (logo, label, text, button) with small differences per format keeps the
// launch formats consistent. Sponsor text is always set as text; only https links and images are used.
// Each unit has one link, its button, stretched over the whole unit: one click target, one tab stop.
// A unit lives in its slot's own shadow root (mount), so the site's CSS never reaches it: only the
// site's font, text size and colour come in. The format decides the size, never the site or the owner.

import { el, https } from './dom.js';

/**
 * In-page formats and the height (px) each reserves while booked, so the page doesn't jump.
 * @type {Record<string, number>}
 */
export const HEIGHTS = { banner: 72, card: 170, inline: 48, logo: 28 };

/** @type {CSSStyleSheet | undefined} */
let unitSheet;

/**
 * The slot's own corner of the page: an open shadow root with the units' stylesheet and one wrapper,
 * made once. The wrapper is the reset (the site's inherited styles stop there) and carries the format,
 * which sets how wide the unit and its open spot may grow.
 * @param {HTMLElement} host  the slot's <sl-slot> (or the booking page's preview box)
 * @param {string} format  '' for a leaderboard
 * @param {CSSStyleSheet[]} [sheets]  more styles for this root (the booking page's preview)
 * @returns {HTMLElement} the wrapper to draw in
 */
export function mount(host, format, sheets = []) {
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    let u = /** @type {HTMLElement | null} */ (root.firstElementChild);
    if (!u) {
        if (!unitSheet) {
            unitSheet = new CSSStyleSheet();
            unitSheet.replaceSync(UNIT_CSS);
        }
        root.adoptedStyleSheets = [unitSheet, ...sheets];
        u = root.appendChild(el('div'));
    }
    u.className = 'sl-u f-' + (format || 'board');
    return u;
}

/**
 * @param {import('./config.js').Sponsor} s
 * @param {string} format
 * @returns {HTMLElement}
 */
export function creative(s, format) {
    const { brand, text = '' } = s, pic = https(s.image), c = 'sl-c ';
    /** @param {string} cls @param {...(Node | string)} kids */
    const link = (cls, ...kids) => {
        const a = el('a', cls, ...kids), href = https(s.href);
        if (href) Object.assign(a, { href, target: '_blank', rel: 'sponsored noopener' });
        return a;
    };
    const name = () => el('b', '', brand);
    const tag = (label = 'Sponsor') => el('span', 'sl-tag', label);
    const dim = (tagName = /** @type {'span' | 'div'} */ ('span')) => el(tagName, 'sl-dim', text);
    // the button is the unit's one link; its name says whose it is, since it reads alone in a list of links
    const cta = (label = s.cta || 'Learn more', arrow = '', cls = 'sl-cta') => {
        const a = link(cls, label + arrow);
        a.setAttribute('aria-label', `${label}, ${brand}`);
        return a;
    };
    const image = () => Object.assign(el('img'), { src: pic, alt: brand });
    /** @param {number} px */
    const logo = px => {
        const src = https(s.logo);
        if (src) return Object.assign(el('img', 'sl-logo'), { src, width: px, height: px, alt: '' });
        const e = el('span', 'sl-logo', brand[0] || '?');
        const bg = /^#[\da-f]{3,8}$/i.test(s.color || '') ? String(s.color) : '#71717a';
        Object.assign(e.style, { background: bg, width: px + 'px', height: px + 'px', fontSize: px / 2.2 + 'px' });
        return e;
    };
    switch (format) {
        case 'bar': return el('div', c + 'sl-top', tag(), logo(22), name(), dim(), cta());
        case 'banner': return pic
            ? link(c + 'sl-wideimg', image(), tag('Sponsor · ' + brand))
            : el('div', c + 'sl-wide', logo(40), el('div', '', tag(), el('div', '', name(), ' — ', text)), cta());
        case 'card': return el('div', c + 'sl-box', tag(), pic && el('span', 'sl-cardimg', image()), el('div', 'sl-row', logo(40), name()), el('div', '', text), cta());
        case 'inline': return el('p', c + 'sl-line', tag(), ' ', name(), ': ', text, ' ', cta(s.cta || 'Take a look', ' →', 'sl-go'));
        case 'logo': return link(c + 'sl-logos', el('span', 'sl-dim', 'Sponsored by'), logo(24), name());
    }
    return el('div');
}

/**
 * The open spot on an empty slot, drawn in its format's own layout ("Sponsor", a dashed logo, "Your brand
 * here", "Sponsor this spot · $49/month · via Sponsor.st", a "Book this spot" button), so it takes the same
 * box a booked sponsor will: the page doesn't jump when a month sells, and a brand sees the real size.
 * Its own class (sl-o, not sl-c): a click on it goes to the booking page and is never counted as a
 * sponsor click. A top bar keeps its pinned one-line shell, and a note for the owner with no booking page
 * (a slot visitors don't see) stays one compact line.
 * @param {import('./config.js').Placeholder} p
 * @param {string} format
 */
export function placeholder(p, format) {
    /** @param {string} cls @param {string} text @param {unknown} url */
    const link = (cls, text, url) => {
        const a = el('a', cls, text), href = https(url);
        if (href) a.href = href;
        return a;
    };
    // " · via Sponsor.st" wraps as one piece (a narrow column never leaves a lone "·"); its space is a
    // no-break one, which a flex layout keeps
    const via = () => p.via && el('span', 'sl-v', ' · ', link('sl-via', 'via Sponsor.st', p.via));
    const offer = () => el('span', '', p.text, via());
    const book = (cls = 'sl-ob sl-cta', arrow = '') => link(cls, 'Book this spot' + arrow, p.href);
    const name = () => el('b', '', 'Your brand here');
    const tag = () => el('span', 'sl-tag', 'Sponsor');
    /** @param {number} px */
    const plus = px => {
        const e = el('span', 'sl-logo sl-plus', '+');
        Object.assign(e.style, { width: px + 'px', height: px + 'px', fontSize: px / 2 + 'px' });
        return e;
    };
    // a note for the owner (a slot visitors don't see, saying why) has no booking page: one compact line
    if (format === 'bar' || !https(p.href)) return el('div', format === 'bar' ? 'sl-o sl-top' : 'sl-o', link('sl-ob', p.text, p.href), via());
    const o = 'sl-o sl-open ';
    switch (format) {
        case 'card': return el('div', o + 'sl-box', tag(), el('div', 'sl-row', plus(40), name()), el('div', '', offer()), book());
        case 'inline': return el('p', o + 'sl-line', tag(), ' ', offer(), ' ', book('sl-ob sl-go', ' →'));
        case 'logo': return el('div', o + 'sl-logos', plus(24), link('sl-ob', p.text, p.href), via());
    }
    return el('div', o + 'sl-wide', plus(40), el('div', '', tag(), el('div', '', offer())), book());
}

// The page's own stylesheet holds only how the boxes sit in the page; adopted by the document (a strict
// style-src CSP allows that; <style> tags it blocks). No "ad", "sponsor" or "banner" in names, so ad
// blockers leave real content alone. A slot keeps 1em above and below, which collapses into the site's
// own margins; inside a flex or grid layout or a list (data-flat) the site's own gaps space it instead.
// A box shows once something is drawn in it (data-on).
export const CSS = `
sl-slot,sl-row{display:block;position:relative;margin:1em 0}
:is(sl-slot,sl-row):first-child{margin-top:0}
:is(sl-slot,sl-row):last-child{margin-bottom:0}
ul>sl-slot,ol>sl-slot{display:list-item}
sl-slot:not([data-on]){display:none}
sl-row{display:flex;flex-wrap:wrap;gap:12px;align-items:stretch}
sl-row>sl-slot{flex:1 1 240px;min-width:0;margin:0}
sl-row[data-stack]{flex-direction:column;flex-wrap:nowrap}
sl-row[data-stack]>sl-slot{flex:none}
sl-slot[data-flat],sl-row[data-flat]{margin:0}
body.sl-hastop{padding-top:40px!important}
`;

// Inside each slot. Formats grow to their width and no further, centred beyond it; in a grid cell
// (data-flat=cell) every unit and open spot fills the cell, its content centred.
const UNIT_CSS = `
:host{--sl-line:rgba(0,0,0,.12);--sl-card:rgba(255,255,255,.65);--sl-soft:rgba(0,0,0,.04);--sl-dash:rgba(0,0,0,.3);--sl-ring:#18181b}
:host([data-sl-theme=dark]){--sl-line:rgba(255,255,255,.18);--sl-card:rgba(255,255,255,.06);--sl-soft:rgba(255,255,255,.07);--sl-dash:rgba(255,255,255,.32);--sl-ring:#f4f4f5}
.sl-u{all:initial;display:block;font-family:inherit;font-size:inherit;color:inherit;line-height:1.4;overflow-wrap:anywhere}
.f-banner{max-width:970px;margin:0 auto}
.f-card{max-width:400px;margin:0 auto}
:host([data-flat=cell]) .sl-u{height:100%;max-width:none}
.sl-u *{box-sizing:border-box}
.sl-c,.sl-o{position:relative;min-width:0;max-width:100%;text-align:start}
.sl-c{font-size:clamp(13px,.875em,16px)}
.sl-c img{max-width:100%;border:0}
a{color:inherit;text-decoration:none}
:focus-visible{outline:none}
a.sl-c:focus-visible,.sl-cta:focus-visible::after,.sl-go:focus-visible::after,.sl-ob:focus-visible::after,.sl-u .sl-via:focus-visible,.sl-u .sl-bb:focus-visible,.sl-u .sl-bv:focus-visible{outline:2px solid var(--sl-ring);outline-offset:2px}
.sl-tag{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em}
.sl-logo{border-radius:calc(var(--sl-r) * .75);display:inline-grid;place-items:center;color:#fff;font-weight:700;flex:none;object-fit:cover}
.sl-row{display:flex;align-items:center;gap:10px}
.sl-cta{margin-left:auto;display:inline-block;padding:7px 13px;border-radius:calc(var(--sl-r) * .75);background:var(--sl-btn);color:var(--sl-btnfg);font-size:.93em;line-height:1.3;font-weight:500;white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis}
.sl-cta::after,.sl-go::after,.sl-ob::after{content:"";position:absolute;inset:0;border-radius:var(--sl-r)}
.sl-wide,.sl-box{border:1px solid var(--sl-line);border-radius:var(--sl-r);background:var(--sl-card);color:var(--sl-fg,inherit)}
.sl-wide:hover,.sl-box:hover,.sl-o:hover{border-color:var(--sl-dash)}
.sl-wide{display:flex;flex-wrap:wrap;align-items:center;gap:12px 16px;padding:14px 18px}
.sl-wide>div{flex:1;min-width:min(180px,100%)}
.sl-wideimg{display:block;position:relative;border-radius:var(--sl-r);overflow:hidden;line-height:0}
.sl-wideimg img{width:100%;aspect-ratio:4/1;object-fit:cover;display:block}
.sl-wideimg .sl-tag{position:absolute;left:8px;bottom:8px;line-height:1.4;background:rgba(0,0,0,.72);color:#fff;padding:2px 7px;border-radius:4px}
.sl-box{display:flex;flex-direction:column;gap:10px;padding:16px}
.sl-box .sl-cta{margin:4px 0 0;text-align:center}
.sl-cardimg{display:block;line-height:0}
.sl-cardimg img{width:100%;aspect-ratio:16/9;object-fit:cover;border-radius:calc(var(--sl-r) * .75)}
.sl-line{margin:0;border-left:3px solid currentColor;padding:10px 14px;background:var(--sl-soft);border-radius:0 var(--sl-r) var(--sl-r) 0}
.sl-go,.sl-via{text-decoration:underline;text-underline-offset:2px}
.sl-go{font-weight:600}
.sl-logos{display:flex;align-items:center;gap:10px;width:fit-content;margin:0 auto}
.sl-o{display:block;padding:10px 14px;border:1px dashed var(--sl-dash);border-radius:var(--sl-r);text-align:center;font-size:clamp(12px,.8125em,15px)}
.sl-open{text-align:start;font-size:clamp(13px,.875em,16px);background:transparent;border:1px dashed var(--sl-dash)}
.sl-open.sl-box{display:flex;padding:16px}
.sl-open.sl-wide{display:flex;padding:14px 18px}
.sl-open.sl-line{border:0;border-left:3px dashed var(--sl-dash);border-radius:0 var(--sl-r) var(--sl-r) 0}
.sl-open.sl-logos{display:flex;flex-wrap:wrap;justify-content:center;border:0;padding:0}
.sl-plus{border:1px dashed var(--sl-dash);color:var(--sl-dash);font-weight:400}
.sl-open .sl-cta{background:transparent;color:inherit;box-shadow:inset 0 0 0 1px var(--sl-dash)}
.sl-ob{font-weight:600}
.sl-v{white-space:nowrap}
.sl-via{position:relative;z-index:1}
:host([data-flat=cell]) :is(.sl-o,.sl-wide,.sl-box){height:100%}
:host([data-flat=cell]) .sl-o:not(.sl-open){display:flex;flex-wrap:wrap;align-items:center;justify-content:center}
:host([data-flat=cell]) .sl-box{justify-content:center}
:host([data-flat=cell]) .sl-wide{align-content:center}
.sl-top{position:fixed;top:0;left:0;right:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;gap:12px;height:40px;padding:8px 16px;overflow:hidden;background:#111;color:#fff;font-size:13px;min-width:0;max-width:100%;border:0;border-radius:0;--sl-ring:#fff}
.sl-top .sl-cta:focus-visible::after,.sl-top .sl-ob:focus-visible::after{outline-offset:-4px}
.sl-top .sl-cta{margin:0;background:#fff;color:#111}
.sl-top>*{flex-shrink:0;white-space:nowrap}
.sl-top>b{max-width:35%;overflow:hidden;text-overflow:ellipsis}
.sl-top .sl-dim{flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;opacity:.8}
.sl-top.sl-o{display:block;height:40px;line-height:24px;text-align:center;white-space:nowrap;text-overflow:ellipsis}
@media (max-width:600px){.sl-top .sl-dim{display:none}.sl-top>b{flex-shrink:1;min-width:0}}
`;
